<template>
  <div class="card ytd-card">
    <div class="card-hdr">
      <span class="card-ttl">Year to Date</span>
      <span v-if="subtitle" class="card-sub">{{ subtitle }}</span>
    </div>
    <div class="card-body">
      <div v-if="isLoading" class="ytd-state">
        <DxLoadIndicator :height="24" :width="24" />
      </div>

      <div v-else-if="hasError" class="ytd-state ytd-error">
        <div class="ytd-error-msg">Unable to load Year to Date data.</div>
        <DxButton text="Retry" @click="load" />
      </div>

      <div v-else class="ytd">
        <div
          v-for="tile in tiles"
          :key="tile.key"
          class="ytd-tile"
        >
          <div class="ytd-main">
            <div class="ytd-lbl">{{ tile.label }}</div>
            <div class="ytd-val">{{ tile.value }}</div>
            <div class="ytd-trend" :class="tile.trendClass">
              <svg
                v-if="tile.trendDirection === 'up'"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
              >
                <polyline
                  points="1,8 5,2 9,8"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linejoin="round"
                  stroke-linecap="round"
                />
              </svg>
              <svg
                v-else-if="tile.trendDirection === 'down'"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
              >
                <polyline
                  points="1,2 5,8 9,2"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linejoin="round"
                  stroke-linecap="round"
                />
              </svg>
              <span>{{ tile.trendText }}</span>
            </div>
          </div>

          <svg
            v-if="tile.sparkline"
            class="ytd-spark"
            viewBox="0 0 62 26"
            fill="none"
            preserveAspectRatio="none"
          >
            <polyline
              v-if="tile.sparkline.lastYearPoints"
              :points="tile.sparkline.lastYearPoints"
              stroke="#c4d4e1"
              stroke-width="1"
              fill="none"
            />
            <polyline
              v-if="tile.sparkline.thisYearPoints"
              :points="tile.sparkline.thisYearPoints"
              stroke="#35baf6"
              stroke-width="1.5"
              fill="none"
            />
            <circle
              v-if="tile.sparkline.thisYearEnd"
              :cx="tile.sparkline.thisYearEnd.x"
              :cy="tile.sparkline.thisYearEnd.y"
              r="2"
              fill="#35baf6"
            />
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DxButton } from "devextreme-vue/button";
import { DxLoadIndicator } from "devextreme-vue/load-indicator";
import { CustomerPerformanceService } from "../../_services/api/customers/customer-performance.service";

const SPARK_WIDTH = 62;
const SPARK_HEIGHT = 26;
const SPARK_PAD_Y = 3;

export default {
  name: "GcDashboardYtdCard",
  components: { DxButton, DxLoadIndicator },
  props: {
    customerCode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      hasError: false,
      ytd: null,
      monthly: null,
    };
  },
  computed: {
    subtitle() {
      if (!this.ytd || !this.ytd.ThisYear) return "";
      const thisYear = parseInt(this.ytd.ThisYear, 10);
      if (isNaN(thisYear)) return "";
      const prevYear = thisYear - 1;
      const thisYearShort = String(thisYear).slice(-2);
      return `FY ${prevYear}/${thisYearShort} · Full Year`;
    },
    tiles() {
      const ytd = this.ytd || {};

      return [
        {
          key: "totalSales",
          label: "Total Sales",
          value: this.formatCurrencyK(ytd.ThisYearGrossValue),
          ...this.buildTrend(ytd.ThisYearGrossValue, ytd.LastYearGrossValue, false),
          sparkline: this.buildSparkline("GrossValue"),
        },
        {
          key: "grossProfit",
          label: "Gross Profit",
          value: this.formatCurrencyK(ytd.ThisYearMarginValue),
          ...this.buildTrend(ytd.ThisYearMarginValue, ytd.LastYearMarginValue, false),
          sparkline: this.buildSparkline("MarginValue"),
        },
        {
          key: "gpPct",
          label: "GP %",
          value: this.formatPercent(ytd.ThisYearMarginPcnt),
          ...this.buildTrend(ytd.ThisYearMarginPcnt, ytd.LastYearMarginPcnt, true),
          sparkline: this.buildSparkline("MarginPcnt"),
        },
      ];
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.isLoading = true;
      this.hasError = false;
      try {
        const [ytdRes, monthlyRes] = await Promise.all([
          CustomerPerformanceService.getYtd(this.customerCode),
          CustomerPerformanceService.getMonthly(this.customerCode),
        ]);
        this.ytd = ytdRes.data || null;
        this.monthly = monthlyRes.data || null;
      } catch (err) {
        this.hasError = true;
      } finally {
        this.isLoading = false;
      }
    },
    async refresh() {
      await this.load();
    },
    formatCurrencyK(value) {
      if (value === null || value === undefined) return "—";
      const num = Number(value);
      if (num === 0) return "$0";
      const rounded = Math.round(num / 1000);
      const sign = num < 0 ? "-" : "";
      const absK = Math.abs(rounded);
      return `${sign}$${absK.toLocaleString("en-AU")}k`;
    },
    formatPercent(value) {
      if (value === null || value === undefined) return "—";
      return `${Number(value).toFixed(1)}%`;
    },
    buildTrend(thisYear, lastYear, isPercentageMetric) {
      if (
        thisYear === null ||
        thisYear === undefined ||
        lastYear === null ||
        lastYear === undefined ||
        Number(lastYear) === 0
      ) {
        return {
          trendText: "N/A vs prior year",
          trendClass: "t-neutral",
          trendDirection: "none",
        };
      }
      const diff = isPercentageMetric
        ? Number(thisYear) - Number(lastYear)
        : ((Number(thisYear) - Number(lastYear)) / Number(lastYear)) * 100;
      const rounded = diff.toFixed(1);
      const sign = diff >= 0 ? "+" : "";
      const unit = isPercentageMetric ? "pp" : "%";
      return {
        trendText: `${sign}${rounded}${unit} vs prior year`,
        trendClass: diff >= 0 ? "t-up" : "t-down",
        trendDirection: diff >= 0 ? "up" : "down",
      };
    },
    buildSparkline(field) {
      if (!this.monthly) return null;

      const thisYearRows = (this.monthly.ThisYear || [])
        .slice()
        .sort((a, b) => a.MonthSortOrder - b.MonthSortOrder);
      const lastYearRows = (this.monthly.LastYear || [])
        .slice()
        .sort((a, b) => a.MonthSortOrder - b.MonthSortOrder);

      if (thisYearRows.length === 0 && lastYearRows.length === 0) return null;

      const thisYearValues = thisYearRows.map((r) => Number(r[field]) || 0);
      const lastYearValues = lastYearRows.map((r) => Number(r[field]) || 0);

      const allValues = thisYearValues.concat(lastYearValues);
      const min = Math.min(...allValues);
      const max = Math.max(...allValues);
      const range = max - min || 1;

      const totalMonths = Math.max(thisYearValues.length, lastYearValues.length, 1);
      const xStep = totalMonths > 1 ? SPARK_WIDTH / (totalMonths - 1) : SPARK_WIDTH;

      const mapPoints = (values) => {
        if (!values.length) return null;
        return values
          .map((v, i) => {
            const x = totalMonths === 1 ? SPARK_WIDTH / 2 : i * xStep;
            const y =
              SPARK_HEIGHT -
              SPARK_PAD_Y -
              ((v - min) / range) * (SPARK_HEIGHT - SPARK_PAD_Y * 2);
            return `${x.toFixed(1)},${y.toFixed(1)}`;
          })
          .join(" ");
      };

      const thisYearPoints = mapPoints(thisYearValues);
      const lastYearPoints = mapPoints(lastYearValues);

      let thisYearEnd = null;
      if (thisYearValues.length) {
        const lastIdx = thisYearValues.length - 1;
        const x = totalMonths === 1 ? SPARK_WIDTH / 2 : lastIdx * xStep;
        const y =
          SPARK_HEIGHT -
          SPARK_PAD_Y -
          ((thisYearValues[lastIdx] - min) / range) * (SPARK_HEIGHT - SPARK_PAD_Y * 2);
        thisYearEnd = { x: x.toFixed(1), y: y.toFixed(1) };
      }

      return { thisYearPoints, lastYearPoints, thisYearEnd };
    },
  },
};
</script>

<style lang="scss" scoped>
.ytd-card {
  background: #fff;
  border: 1px solid var(--base-border-color, #e0e0e0);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  min-width: 0;

  .card-hdr {
    padding: 8px 12px;
    border-bottom: 1px solid var(--base-border-color, #edf0f3);
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    gap: 8px;
  }

  .card-ttl {
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .card-sub {
    font-size: 10px;
    color: #667085;
  }

  .card-body {
    padding: 10px 12px;
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .ytd-state {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 24px 0;
  }

  .ytd-error {
    flex-direction: column;

    .ytd-error-msg {
      color: #e8384f;
      font-size: 12px;
    }
  }

  .ytd {
    display: flex;
    flex-direction: column;
    gap: 7px;
    height: 100%;
  }

  .ytd-tile {
    flex: 1;
    background: #f9fafb;
    border: 1px solid #eceff2;
    border-radius: 6px;
    padding: 8px 11px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .ytd-main {
    flex: 1;
    min-width: 0;
  }

  .ytd-lbl {
    font-size: 10px;
    color: #667085;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  .ytd-val {
    font-size: 20px;
    font-weight: 700;
    color: #323842;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
    margin-top: 2px;
  }

  .ytd-trend {
    display: flex;
    align-items: center;
    gap: 3px;
    font-size: 10.5px;
    margin-top: 2px;

    &.t-up {
      color: #027a48;
    }

    &.t-down {
      color: #d92d20;
    }

    &.t-neutral {
      color: #667085;
    }
  }

  .ytd-spark {
    width: 62px;
    height: 26px;
    flex-shrink: 0;
  }
}
</style>
